import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, ShoppingBag, CalendarDays, Home, UtensilsCrossed, Images, Globe, CalendarOff } from "lucide-react";
import { useNewItemAlert, ConfirmDelete } from "./AdminAuth";
import { OrderCard, DeleteBtn, statusPill } from "./AdminListPage";
import { OrderEditModal } from "./OrderEditModal";
import AdminBar from "./AdminBar";
import { markSeenIds, unseenCount } from "./siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

function formatError(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
}

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const fmtDMY = (iso) => iso.split("-").reverse().join("/");

export default function AdminPage() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const doneCount = orders.filter((o) => o.status === "done").length;
  const visibleOrders = showDone
    ? orders
    : orders.filter((o) => o.status !== "done");
  const unseenOrders = unseenCount(orders, "orders");
  const unseenReservations = unseenCount(reservations, "reservations");
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const todayReservations = reservations.filter(
    (r) => r.date === todayStr && r.status !== "annulee"
  );
  const todayGuests = todayReservations.reduce((s, r) => s + (r.guests || 0), 0);
  const orderDay = (o) => o.date || o.created_at.slice(0, 10);
  const todayOrders = orders.filter((o) => orderDay(o) === todayStr);
  const todayOrdersTotal = todayOrders.reduce((s, o) => s + (o.total || 0), 0);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
  const tomorrowReservations = reservations.filter(
    (r) => r.date === tomorrowStr && r.status !== "annulee"
  );
  const tomorrowGuests = tomorrowReservations.reduce(
    (s, r) => s + (r.guests || 0),
    0
  );
  const tomorrowOrders = orders.filter((o) => orderDay(o) === tomorrowStr);
  const tomorrowOrdersTotal = tomorrowOrders.reduce(
    (s, o) => s + (o.total || 0),
    0
  );
  const tomorrowLabel = tomorrow.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const todayLabel = now.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const [dish, setDish] = useState({
    name: "",
    description: "",
    price: 0,
    image: "",
    active: true,
  });

  useNewItemAlert(orders.length, reservations.length);

  const setOrderStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/orders/${id}/status`,
        { status },
        { withCredentials: true }
      );
      setOrders((prev) =>
        prev.map((o) => (o.id === id ? { ...o, status } : o))
      );
    } catch {
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  const setReservationStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/reservations/${id}/status`,
        { status },
        { withCredentials: true }
      );
      setReservations((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status } : r))
      );
    } catch {
      toast.error("Impossible de mettre à jour le statut.");
    }
  };

  const [toDelete, setToDelete] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/${toDelete.tipo}/${toDelete.id}`, {
        withCredentials: true,
      });
      if (toDelete.tipo === "orders")
        setOrders((prev) => prev.filter((o) => o.id !== toDelete.id));
      else setReservations((prev) => prev.filter((r) => r.id !== toDelete.id));
      toast.success("Supprimé.");
    } catch {
      toast.error("Impossible de supprimer.");
    }
    setToDelete(null);
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const load = useCallback(async () => {
    const [o, r, d] = await Promise.all([
      axios.get(`${API}/orders`, { withCredentials: true }),
      axios.get(`${API}/reservations`, { withCredentials: true }),
      axios.get(`${API}/dish-of-day`),
    ]);
    setOrders(o.data);
    setReservations(r.data);
    setDish(d.data);
    setLastUpdate(new Date());
    if (localStorage.getItem("jlv_seen_ids_orders") === null)
      markSeenIds("orders", o.data.map((x) => x.id));
    if (localStorage.getItem("jlv_seen_ids_reservations") === null)
      markSeenIds("reservations", r.data.map((x) => x.id));
  }, []);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
    const id = setInterval(() => load().catch(() => {}), 15000);
    return () => clearInterval(id);
  }, [user, load]);

  useEffect(() => {
    if (user && location.hash) {
      const el = document.querySelector(location.hash);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [user, location.hash]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await axios
      .post(`${API}/auth/logout`, {}, { withCredentials: true })
      .catch(() => {});
    setUser(false);
    toast.success("Session terminée");
  };

  const saveDish = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/dish-of-day`,
        { ...dish, price: Number(dish.price) },
        { withCredentials: true }
      );
      toast.success("Plat du jour mis à jour !");
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const toggleDishActive = async () => {
    const next = { ...dish, price: Number(dish.price), active: !dish.active };
    try {
      const { data } = await axios.put(`${API}/dish-of-day`, next, {
        withCredentials: true,
      });
      setDish(data);
      toast.success(
        data.active
          ? "Plat du jour affiché sur le site."
          : "Plat du jour masqué du site."
      );
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const [dishUploading, setDishUploading] = useState(null);

  const uploadDishImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setDishUploading(0);
    try {
      const { data } = await axios.post(`${API}/upload-image`, fd, {
        withCredentials: true,
        onUploadProgress: (e) =>
          setDishUploading(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      setDish((prev) => ({ ...prev, image: data.url }));
      toast.success("Photo téléchargée ! Pensez à Enregistrer.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible d'envoyer l'image."
      );
    } finally {
      setDishUploading(null);
    }
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB]">
        <p className="text-xs uppercase tracking-[0.3em] text-[#102C1F]/60">
          Chargement...
        </p>
      </div>
    );
  }

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-8"
        >
          <p className="mb-2 text-xs uppercase tracking-[0.3em] text-[#102C1F]/60">
            J'aime la vie
          </p>
          <h1 className="font-display text-3xl italic tracking-tight text-[#102C1F]">
            Espace de Gestion
          </h1>
          <div className="mt-6 space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={field}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              required
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={field}
            />
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit-btn"
              type="submit"
              className="w-full rounded-full bg-[#102C1F] py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
            >
              Se connecter
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F4F1EB] text-[#102C1F]">
      <AdminBar />
      <header className="admin-offset fixed inset-x-0 top-0 z-50 bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <span className="font-display text-xl italic md:text-2xl">
            J'aime la vie — Gestion
          </span>
          <div className="flex items-center gap-4">
            {lastUpdate && (
              <span
                data-testid="admin-last-update"
                className="hidden text-xs uppercase tracking-[0.2em] text-[#F4F1EB]/60 sm:inline"
              >
                Mis à jour {lastUpdate.toLocaleTimeString("fr-FR")}
              </span>
            )}
            <Link
              data-testid="admin-home-link"
              to="/"
              className="flex items-center gap-2 rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
            >
              <Home size={14} /> Accueil
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <section
        data-testid="day-summary"
        className="mx-auto max-w-7xl px-5 pt-28 md:px-10"
      >
        <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
          <div className="rounded-sm bg-[#102C1F] px-6 py-6 text-[#F4F1EB] md:px-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#F4F1EB]/60">
              Résumé du jour
            </p>
            <h2 className="mt-1 font-display text-2xl italic">{todayLabel}</h2>
            <div className="mt-5 flex flex-wrap items-center gap-8 border-t border-[#F4F1EB]/15 pt-5">
              <div data-testid="day-summary-tables" className="text-right">
                <p className="font-display text-3xl italic">
                  {todayReservations.length}
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#F4F1EB]/60">
                  {todayReservations.length === 1 ? "table" : "tables"}
                </p>
              </div>
              <div data-testid="day-summary-guests" className="text-right">
                <p className="font-display text-3xl italic">{todayGuests}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#F4F1EB]/60">
                  {todayGuests === 1 ? "personne" : "personnes"}
                </p>
              </div>
              <span className="hidden h-10 w-px bg-[#F4F1EB]/20 sm:block" />
              <div data-testid="day-summary-orders" className="text-right">
                <p className="font-display text-3xl italic">
                  {todayOrders.length}
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#F4F1EB]/60">
                  {todayOrders.length === 1 ? "commande" : "commandes"}
                </p>
              </div>
              <div data-testid="day-summary-revenue" className="text-right">
                <p className="font-display text-3xl italic">
                  €{todayOrdersTotal.toFixed(2)}
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#F4F1EB]/60">
                  total
                </p>
              </div>
            </div>
          </div>
          <div
            data-testid="day-summary-tomorrow"
            className="rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] px-6 py-6 text-[#102C1F] md:px-8"
          >
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#102C1F]/60">
              Demain
            </p>
            <h2 className="mt-1 font-display text-2xl italic">
              {tomorrowLabel}
            </h2>
            <div className="mt-5 flex flex-wrap items-center gap-8 border-t border-[#102C1F]/10 pt-5">
              <div
                data-testid="day-summary-tomorrow-tables"
                className="text-right"
              >
                <p className="font-display text-3xl italic">
                  {tomorrowReservations.length}
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#102C1F]/60">
                  {tomorrowReservations.length === 1 ? "table" : "tables"}
                </p>
              </div>
              <div
                data-testid="day-summary-tomorrow-guests"
                className="text-right"
              >
                <p className="font-display text-3xl italic">{tomorrowGuests}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#102C1F]/60">
                  {tomorrowGuests === 1 ? "personne" : "personnes"}
                </p>
              </div>
              <span className="hidden h-10 w-px bg-[#102C1F]/15 sm:block" />
              <div
                data-testid="day-summary-tomorrow-orders"
                className="text-right"
              >
                <p className="font-display text-3xl italic">
                  {tomorrowOrders.length}
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#102C1F]/60">
                  {tomorrowOrders.length === 1 ? "commande" : "commandes"}
                </p>
              </div>
              <div
                data-testid="day-summary-tomorrow-revenue"
                className="text-right"
              >
                <p className="font-display text-3xl italic">
                  €{tomorrowOrdersTotal.toFixed(2)}
                </p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#102C1F]/60">
                  total
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        id="plat-du-jour"
        data-testid="admin-dish-editor"
        className="mx-auto max-w-7xl scroll-mt-28 px-5 pt-8 md:px-10"
      >
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <h2 className="font-display text-2xl italic">Plat du Jour</h2>
          <button
            type="button"
            data-testid="dish-toggle-active-btn"
            onClick={toggleDishActive}
            className={`rounded-full px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
              dish.active === false
                ? "border border-[#102C1F]/25 text-[#102C1F]/70 hover:border-[#102C1F]"
                : "bg-red-700 text-white hover:bg-red-800"
            }`}
          >
            {dish.active === false ? "Afficher sur le site" : "Masquer du site"}
          </button>
          {dish.active === false && (
            <span
              data-testid="dish-hidden-badge"
              className="rounded-full bg-red-700/10 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-red-700"
            >
              Masqué
            </span>
          )}
        </div>
        <form
          onSubmit={saveDish}
          className="grid gap-4 rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-5 md:grid-cols-2"
        >
          <label className="block">
            <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
              Nom du plat
            </span>
            <input
              data-testid="dish-name-input"
              required
              value={dish.name}
              onChange={(e) => setDish({ ...dish, name: e.target.value })}
              className={field}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
              Prix (€)
            </span>
            <input
              data-testid="dish-price-input"
              required
              type="number"
              step="0.01"
              min="0"
              value={dish.price}
              onChange={(e) => setDish({ ...dish, price: e.target.value })}
              className={field}
            />
          </label>
          <label className="block md:col-span-2">
            <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
              Description
            </span>
            <input
              data-testid="dish-desc-input"
              value={dish.description}
              onChange={(e) =>
                setDish({ ...dish, description: e.target.value })
              }
              className={field}
            />
          </label>
          <div className="block md:col-span-2">
            <span className="mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
              Photo (facultatif)
            </span>
            <label
              data-testid="dish-upload-btn"
              className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#102C1F]/30 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#102C1F]/70 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
            >
              <input
                data-testid="dish-file-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  uploadDishImage(e.target.files[0]);
                  e.target.value = "";
                }}
              />
              {dishUploading != null
                ? `Envoi ${dishUploading}%`
                : "Télécharger une photo"}
            </label>
            <input
              data-testid="dish-image-input"
              placeholder="ou collez une URL https://..."
              value={dish.image}
              onChange={(e) => setDish({ ...dish, image: e.target.value })}
              className={`${field} mt-2`}
            />
          </div>
          <button
            data-testid="dish-save-btn"
            type="submit"
            className="rounded-full bg-[#102C1F] px-7 py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030] md:justify-self-start"
          >
            Enregistrer le plat du jour
          </button>
        </form>
      </section>

      <main className="mx-auto grid max-w-7xl gap-10 px-5 py-12 md:grid-cols-2 md:px-10">
        <section>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <span className="relative inline-flex">
              <Link
                data-testid="orders-page-link"
                to="/admin/encomendas"
                className="flex items-center gap-3 rounded-full border border-[#102C1F]/25 px-5 py-2.5 font-display text-2xl italic transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
              >
                <ShoppingBag size={20} /> Commandes ({visibleOrders.length})
              </Link>
              {unseenOrders > 0 && (
                <span
                  data-testid="orders-unseen-badge"
                  className="pointer-events-none absolute -right-2.5 -top-2.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-600 px-1.5 text-xs font-bold text-white shadow-md"
                >
                  {unseenOrders}
                </span>
              )}
            </span>
            {doneCount > 0 && (
              <button
                data-testid="toggle-done-orders-btn"
                onClick={() => setShowDone(!showDone)}
                className="text-xs uppercase tracking-[0.2em] text-[#102C1F]/60 underline-offset-4 transition-colors duration-300 hover:text-[#102C1F] hover:underline"
              >
                {showDone
                  ? "Masquer les remises"
                  : `Voir les remises (${doneCount})`}
              </button>
            )}
          </div>
          <div data-testid="admin-orders-list" className="space-y-4">
            {visibleOrders.length === 0 && (
              <p className="text-sm text-[#102C1F]/60">
                Aucune commande en cours.
              </p>
            )}
            {visibleOrders.slice(0, 3).map((o, i) => (
              <OrderCard
                key={o.id}
                o={o}
                i={i}
                onStatus={setOrderStatus}
                onDelete={(id, name) =>
                  setToDelete({ tipo: "orders", id, name })
                }
                onEdit={setEditingOrder}
              />
            ))}
          </div>
        </section>

        <section>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <span className="relative inline-flex">
              <Link
                data-testid="reservations-page-link"
                to="/admin/reservas"
                className="flex items-center gap-3 rounded-full border border-[#102C1F]/25 px-5 py-2.5 font-display text-2xl italic transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
              >
                <CalendarDays size={20} /> Réservations ({reservations.length})
              </Link>
              {unseenReservations > 0 && (
                <span
                  data-testid="reservations-unseen-badge"
                  className="pointer-events-none absolute -right-2.5 -top-2.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-600 px-1.5 text-xs font-bold text-white shadow-md"
                >
                  {unseenReservations}
                </span>
              )}
            </span>
          </div>
          <div data-testid="admin-reservations-list" className="space-y-4">
            {reservations.length === 0 && (
              <p className="text-sm text-[#102C1F]/60">
                Aucune réservation pour le moment.
              </p>
            )}
            {reservations.slice(0, 3).map((r, i) => (
              <article
                key={r.id}
                data-testid={`admin-reservation-card-${i}`}
                className="rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-5"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-medium">{r.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#102C1F]/55">
                      {fmtDate(r.created_at)}
                    </span>
                    <DeleteBtn
                      testid={`delete-reservation-${i}`}
                      onClick={() =>
                        setToDelete({
                          tipo: "reservations",
                          id: r.id,
                          name: r.name,
                        })
                      }
                    />
                  </div>
                </div>
                <p className="mt-1 text-sm text-[#102C1F]/70">
                  {r.phone}
                  {r.email ? ` · ${r.email}` : ""}
                </p>
                <p className="mt-3 border-t border-[#102C1F]/10 pt-3 text-sm">
                  {fmtDMY(r.date)} à {r.time} · {r.guests}{" "}
                  {r.guests === 1 ? "personne" : "personnes"}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[#102C1F]/10 pt-3">
                  <span className="mr-1 text-xs uppercase tracking-[0.2em] text-[#102C1F]/60">
                    Statut
                  </span>
                  <button
                    data-testid={`reservation-status-confirmee-${i}`}
                    onClick={() => setReservationStatus(r.id, "confirmee")}
                    className={statusPill(r.status === "confirmee", "prep")}
                  >
                    Confirmée
                  </button>
                  <button
                    data-testid={`reservation-status-annulee-${i}`}
                    onClick={() => setReservationStatus(r.id, "annulee")}
                    className={statusPill(r.status === "annulee", "cancel")}
                  >
                    Annulée
                  </button>
                  <button
                    data-testid={`reservation-status-terminee-${i}`}
                    onClick={() => setReservationStatus(r.id, "terminee")}
                    className={statusPill(r.status === "terminee", "done")}
                  >
                    Terminée
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>

      <section className="mx-auto max-w-7xl px-5 pb-12 md:px-10">
        <h2 className="mb-6 font-display text-2xl italic">Éditeur</h2>
        <div className="flex flex-wrap gap-3">
          <Link
            data-testid="menu-editor-link"
            to="/admin/carte"
            className="inline-flex items-center gap-3 rounded-full border border-[#102C1F]/25 px-5 py-2.5 font-display text-2xl italic transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            <UtensilsCrossed size={20} /> Carte Emporter
          </Link>
          <Link
            data-testid="portfolio-editor-link"
            to="/admin/portfolio"
            className="inline-flex items-center gap-3 rounded-full border border-[#102C1F]/25 px-5 py-2.5 font-display text-2xl italic transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            <Images size={20} /> Portfolio
          </Link>
          <Link
            data-testid="site-editor-link"
            to="/admin/site"
            className="inline-flex items-center gap-3 rounded-full border border-[#102C1F]/25 px-5 py-2.5 font-display text-2xl italic transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            <Globe size={20} /> Site
          </Link>
          <Link
            data-testid="fermetures-editor-link"
            to="/admin/fermetures"
            className="inline-flex items-center gap-3 rounded-full border border-[#102C1F]/25 px-5 py-2.5 font-display text-2xl italic transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            <CalendarOff size={20} /> Jours fermés
          </Link>
        </div>
      </section>

      {editingOrder && (
        <OrderEditModal
          order={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={load}
        />
      )}

      <ConfirmDelete
        item={toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
